import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useSubscription } from './SubscriptionContext';

const ThemeContext = createContext(null);

export const THEMES = [
  { id: 'emerald', label: { fr: 'Émeraude (Banque)', en: 'Emerald (Bank)', es: 'Esmeralda (Banco)' }, swatch: '#047857' },
  { id: 'indigo', label: { fr: 'Marine', en: 'Navy', es: 'Marino' }, swatch: '#4338ca' },
  { id: 'bleu', label: { fr: 'Bleu', en: 'Blue', es: 'Azul' }, swatch: '#2563eb' },
  { id: 'orange', label: { fr: 'Orange', en: 'Orange', es: 'Naranja' }, swatch: '#ea580c' },
  { id: 'brun', label: { fr: 'Brun', en: 'Brown', es: 'Marrón' }, swatch: '#92400e' },
  { id: 'jaune', label: { fr: 'Jaune', en: 'Yellow', es: 'Amarillo' }, swatch: '#ca8a04' },
  { id: 'gris', label: { fr: 'Gris moyen', en: 'Medium gray', es: 'Gris medio' }, swatch: '#6b7280' },
  { id: 'noir', label: { fr: 'Noir', en: 'Black', es: 'Negro' }, swatch: '#1f2937' },
];

const STORAGE_KEY = 'cap360_theme';

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};

export const ThemeProvider = ({ children }) => {
  const { isFree } = useSubscription();
  const [theme, setTheme] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && THEMES.find(t => t.id === saved)) return saved;
    } catch {
      // ignore
    }
    return 'emerald';
  });

  // En mode FREE, le thème est verrouillé sur 'emerald'.
  const effectiveTheme = isFree ? 'emerald' : theme;

  useEffect(() => {
    document.body.setAttribute('data-theme', effectiveTheme);
    try { localStorage.setItem(STORAGE_KEY, theme); } catch { /* ignore */ }
  }, [theme, effectiveTheme]);

  const changeTheme = useCallback((id) => {
    if (isFree) return; // verrouillage côté provider
    if (THEMES.find(t => t.id === id)) setTheme(id);
  }, [isFree]);

  const value = useMemo(
    () => ({ theme: effectiveTheme, changeTheme, themes: THEMES, locked: isFree }),
    [effectiveTheme, changeTheme, isFree]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
